import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import axios from 'axios';
import { buildApiUrl } from '../../config/api';
import '../../styles/adminbooking.css';

const AdminBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        payment_status: '',
        search: '',
        from_date: '',
        to_date: ''
    });
    const [showFilters, setShowFilters] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [dateRange, setDateRange] = useState('all');

    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('❌ No token found');
            return null;
        }
        return token;
    };

    const normalizeBookingsResponse = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.bookings)) return payload.bookings;
        if (Array.isArray(payload?.data?.bookings)) return payload.data.bookings;
        return [];
    };

    const normalizePaymentStatus = useCallback((booking) => {
        const rawStatus = String(booking?.payment_status || '').trim().toLowerCase();
        const transactionId = String(booking?.transaction_id || '').trim();

        if (['completed', 'paid', 'captured', 'success'].includes(rawStatus)) {
            return 'completed';
        }

        if (['partial', 'partially_paid', 'partially-paid'].includes(rawStatus)) {
            return 'partial';
        }

        if (['failed', 'refunded'].includes(rawStatus)) {
            return rawStatus;
        }

        if (!rawStatus && transactionId) {
            return 'completed';
        }

        return 'pending';
    }, []);

    const normalizeBookingRecord = useCallback((booking) => {
        const normalizedPaymentStatus = normalizePaymentStatus(booking);
        const rawBookingStatus = String(booking?.booking_status || '').trim().toLowerCase();
        const normalizedBookingStatus = rawBookingStatus || (normalizedPaymentStatus === 'completed' ? 'confirmed' : 'pending');

        return {
            ...booking,
            payment_status: normalizedPaymentStatus,
            booking_status: normalizedBookingStatus
        };
    }, [normalizePaymentStatus]);

    const getFallbackStats = useCallback((sourceBookings = []) => ({
        total: sourceBookings.length,
        confirmed: sourceBookings.filter((b) => b.booking_status === 'confirmed').length,
        pending_payment: sourceBookings.filter((b) => b.payment_status === 'pending').length,
        revenue: sourceBookings.reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0)
    }), []);

    const getDueAmount = useCallback((booking) => {
        return Math.max(Number(booking?.total_amount || 0) - Number(booking?.advance_amount || 0), 0);
    }, []);

    const fetchBookings = useCallback(async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) {
                window.location.href = '/admin/login';
                return;
            }

            const response = await axios.get(buildApiUrl('/bookings'), {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token,
                    'Content-Type': 'application/json'
                } 
            });
            
            const bookingsData = normalizeBookingsResponse(response.data).map(normalizeBookingRecord);
            
            setBookings(bookingsData);
            setFilteredBookings(bookingsData);
            return bookingsData;
        } catch (error) {
            console.error('Error fetching bookings:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert('Failed to fetch bookings');
            }
            return [];
        } finally {
            setLoading(false);
        }
    }, [normalizeBookingRecord]);

    const fetchStats = useCallback(async (sourceBookings = []) => {
        try {
            const token = getToken();
            if (!token) return;

            const response = await axios.get(buildApiUrl('/bookings/stats'), {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token,
                    'Content-Type': 'application/json'
                } 
            });
            
            let statsData = response.data.data || response.data;
            setStats(statsData);
        } catch (error) {
            console.error('Error fetching stats:', error);
            setStats(getFallbackStats(sourceBookings));
        }
    }, [getFallbackStats]);

    const filterBookings = useCallback(() => {
        let filtered = [...bookings];

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(booking => 
                booking.customer_name?.toLowerCase().includes(searchLower) ||
                booking.customer_email?.toLowerCase().includes(searchLower) ||
                booking.booking_id?.toLowerCase().includes(searchLower) ||
                booking.package_title?.toLowerCase().includes(searchLower)
            );
        }

        if (filters.status) {
            filtered = filtered.filter(booking => booking.booking_status === filters.status);
        }

        if (filters.payment_status) {
            filtered = filtered.filter(booking => normalizePaymentStatus(booking) === filters.payment_status);
        }

        if (filters.from_date) {
            filtered = filtered.filter(booking => new Date(booking.travel_date) >= new Date(filters.from_date));
        }
        if (filters.to_date) {
            filtered = filtered.filter(booking => new Date(booking.travel_date) <= new Date(filters.to_date));
        }

        const today = new Date().toISOString().split('T')[0];
        if (dateRange === 'today') {
            filtered = filtered.filter(booking => booking.travel_date === today);
        } else if (dateRange === 'week') {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            filtered = filtered.filter(booking => new Date(booking.travel_date) >= weekAgo);
        } else if (dateRange === 'month') {
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            filtered = filtered.filter(booking => new Date(booking.travel_date) >= monthAgo);
        }

        setFilteredBookings(filtered);
    }, [bookings, dateRange, filters, normalizePaymentStatus]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Please login to access admin panel');
            window.location.href = '/admin/login';
            return;
        }
        const loadData = async () => {
            const bookingsData = await fetchBookings();
            await fetchStats(bookingsData);
        };
        loadData();
    }, [fetchBookings, fetchStats]);

    useEffect(() => {
        filterBookings();
    }, [filterBookings]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            payment_status: '',
            search: '',
            from_date: '',
            to_date: ''
        });
        setDateRange('all');
        setFilteredBookings(bookings);
    };

    const handleStatusUpdate = async (id, status, paymentStatus, reason = '') => {
        try {
            setUpdating(true);
            const token = getToken();
            if (!token) {
                alert('Please login again');
                window.location.href = '/admin/login';
                return;
            }
            
            await axios.patch(
                buildApiUrl(`/bookings/${id}/status`),
                { booking_status: status, payment_status: paymentStatus, cancellation_reason: reason },
                { headers: { 'Authorization': `Bearer ${token}`, 'x-auth-token': token, 'Content-Type': 'application/json' } }
            );
            
            const refreshedBookings = await fetchBookings();
            await fetchStats(refreshedBookings);
            setShowModal(false);
            alert('✅ Booking status updated successfully');
        } catch (error) {
            console.error('Error updating booking:', error);
            alert(error.response?.data?.message || 'Failed to update booking status');
        } finally {
            setUpdating(false);
        }
    };

    const exportBookings = async () => {
        try {
            const rows = filteredBookings.map((booking) => ({
                'Booking ID': booking.booking_id || '',
                'Customer Name': booking.customer_name || '',
                'Customer Email': booking.customer_email || '',
                'Customer Phone': booking.customer_phone || '',
                'Package': booking.package_title || '',
                'Travel Date': booking.travel_date || '',
                'Adults': booking.adults || 0,
                'Children': booking.children || 0,
                'Total Amount': booking.total_amount || 0,
                'Advance Amount': booking.advance_amount || 0,
                'Payment Method': booking.payment_method || '',
                'Payment Status': booking.payment_status || '',
                'Booking Status': booking.booking_status || '',
                'Created At': booking.created_at || ''
            }));

            const headers = Object.keys(rows[0] || {
                'Booking ID': '',
                'Customer Name': '',
                'Customer Email': '',
                'Customer Phone': '',
                'Package': '',
                'Travel Date': '',
                'Adults': '',
                'Children': '',
                'Total Amount': '',
                'Advance Amount': '',
                'Payment Method': '',
                'Payment Status': '',
                'Booking Status': '',
                'Created At': ''
            });

            const csv = [
                headers.join(','),
                ...rows.map((row) =>
                    headers
                        .map((header) => `"${String(row[header] ?? '').replace(/"/g, '""')}"`)
                        .join(',')
                )
            ].join('\n');

            const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `bookings_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            alert('✅ Export completed successfully');
        } catch (error) {
            console.error('Error exporting bookings:', error);
            alert('Failed to export bookings');
        }
    };

    const getStatusBadgeClass = (status) => {
        const classes = {
            'confirmed': 'status-success',
            'cancelled': 'status-danger',
            'completed': 'status-info',
            'pending': 'status-warning'
        };
        return classes[status] || 'status-secondary';
    };

    const getPaymentBadgeClass = (status) => {
        const classes = {
            'completed': 'payment-success',
            'partial': 'payment-warning',
            'pending': 'payment-danger',
            'failed': 'payment-danger',
            'refunded': 'payment-secondary'
        };
        return classes[status] || 'payment-secondary';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(parseFloat(amount));
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader 
                    title="Package Bookings" 
                    subtitle="Manage and track all customer package bookings"
                />

                {/* Stats Cards */}
                {stats && (
                    <div className="stats-cards">
                        <div className="stat-card">
                            <div className="stat-icon total">
                                <i className="fas fa-calendar-check"></i>
                            </div>
                            <div className="stat-info">
                                <h3>Total Bookings</h3>
                                <p className="stat-value">{stats.total || 0}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon confirmed">
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <div className="stat-info">
                                <h3>Confirmed</h3>
                                <p className="stat-value">{stats.confirmed || 0}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon pending">
                                <i className="fas fa-clock"></i>
                            </div>
                            <div className="stat-info">
                                <h3>Pending Payment</h3>
                                <p className="stat-value">{stats.pending_payment || 0}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon revenue">
                                <i className="fas fa-rupee-sign"></i>
                            </div>
                            <div className="stat-info">
                                <h3>Total Revenue</h3>
                                <p className="stat-value">{formatCurrency(stats.revenue || 0)}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters Section */}
                <div className="filters-section">
                    <div className="filters-header" onClick={() => setShowFilters(!showFilters)}>
                        <div className="filters-title">
                            <i className="fas fa-filter"></i>
                            <h3>Filter Bookings</h3>
                        </div>
                        <button className="toggle-btn">
                            <i className={`fas fa-chevron-${showFilters ? 'up' : 'down'}`}></i>
                        </button>
                    </div>

                    {showFilters && (
                        <div className="filters-body">
                            <div className="quick-filters">
                                <button className={`quick-filter ${dateRange === 'all' ? 'active' : ''}`} onClick={() => setDateRange('all')}>
                                    All Time
                                </button>
                                <button className={`quick-filter ${dateRange === 'today' ? 'active' : ''}`} onClick={() => setDateRange('today')}>
                                    Today
                                </button>
                                <button className={`quick-filter ${dateRange === 'week' ? 'active' : ''}`} onClick={() => setDateRange('week')}>
                                    Last 7 Days
                                </button>
                                <button className={`quick-filter ${dateRange === 'month' ? 'active' : ''}`} onClick={() => setDateRange('month')}>
                                    Last 30 Days
                                </button>
                            </div>

                            <div className="filters-grid">
                                <div className="filter-input-group">
                                    <label><i className="fas fa-search"></i> Search</label>
                                    <input type="text" name="search" placeholder="Name, email or booking ID..." value={filters.search} onChange={handleFilterChange} />
                                </div>
                                <div className="filter-input-group">
                                    <label><i className="fas fa-tag"></i> Booking Status</label>
                                    <select name="status" value={filters.status} onChange={handleFilterChange}>
                                        <option value="">All Status</option>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="pending">Pending</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="filter-input-group">
                                    <label><i className="fas fa-credit-card"></i> Payment Status</label>
                                    <select name="payment_status" value={filters.payment_status} onChange={handleFilterChange}>
                                        <option value="">All Payments</option>
                                        <option value="completed">Completed</option>
                                        <option value="partial">Partial</option>
                                        <option value="pending">Pending</option>
                                    </select>
                                </div>
                                <div className="filter-input-group">
                                    <label><i className="fas fa-calendar-alt"></i> From Date</label>
                                    <input type="date" name="from_date" value={filters.from_date} onChange={handleFilterChange} />
                                </div>
                                <div className="filter-input-group">
                                    <label><i className="fas fa-calendar-alt"></i> To Date</label>
                                    <input type="date" name="to_date" value={filters.to_date} onChange={handleFilterChange} />
                                </div>
                                <div className="filter-actions">
                                    <button className="btn-export" onClick={exportBookings}>
                                        <i className="fas fa-download"></i> Export CSV
                                    </button>
                                    <button className="btn-reset" onClick={clearFilters}>
                                        <i className="fas fa-undo-alt"></i> Reset
                                    </button>
                                </div>
                            </div>

                            {(filters.search || filters.status || filters.payment_status || filters.from_date || filters.to_date || dateRange !== 'all') && (
                                <div className="active-filters">
                                    <span className="active-filters-label">Active Filters:</span>
                                    <div className="filter-tags">
                                        {filters.search && <span className="filter-tag">Search: "{filters.search}" <i className="fas fa-times" onClick={() => setFilters({...filters, search: ''})}></i></span>}
                                        {filters.status && <span className="filter-tag">Status: {filters.status} <i className="fas fa-times" onClick={() => setFilters({...filters, status: ''})}></i></span>}
                                        {filters.payment_status && <span className="filter-tag">Payment: {filters.payment_status} <i className="fas fa-times" onClick={() => setFilters({...filters, payment_status: ''})}></i></span>}
                                        {dateRange !== 'all' && <span className="filter-tag">Period: {dateRange} <i className="fas fa-times" onClick={() => setDateRange('all')}></i></span>}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Results Summary */}
                <div className="results-summary">
                    <div className="results-count">
                        <i className="fas fa-list"></i>
                        <span>Showing <strong>{filteredBookings.length}</strong> of <strong>{bookings.length}</strong> bookings</span>
                    </div>
                    <button className="refresh-btn" onClick={fetchBookings}>
                        <i className="fas fa-sync-alt"></i> Refresh
                    </button>
                </div>

                {/* Bookings Table */}
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading bookings...</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="bookings-table">
                            <thead>
                                <tr>
                                    <th>Booking ID</th>
                                    <th>Customer Details</th>
                                    <th>Package</th>
                                    <th>Travel Date</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Payment</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBookings.length > 0 ? (
                                    filteredBookings.map(booking => (
                                        <tr key={booking.id}>
                                            <td>
                                                <span className="booking-id">{booking.booking_id}</span>
                                            </td>
                                            <td>
                                                <div className="customer-info">
                                                    <div className="customer-avatar">
                                                        {booking.customer_name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="customer-details">
                                                        <div className="customer-name">{booking.customer_name}</div>
                                                        <div className="customer-email">{booking.customer_email}</div>
                                                        <div className="customer-phone">{booking.customer_phone}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="package-name">{booking.package_title || 'N/A'}</span>
                                            </td>
                                            <td>
                                                <div className="travel-date">
                                                    <i className="far fa-calendar-alt"></i>
                                                    {formatDate(booking.travel_date)}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="amount-info">
                                                    <span className="total-amount">{formatCurrency(booking.total_amount)}</span>
                                                    {booking.advance_amount > 0 && (
                                                        <span className="advance-amount">Adv: {formatCurrency(booking.advance_amount)}</span>
                                                    )}
                                                    {getDueAmount(booking) > 0 && (
                                                        <span className="advance-amount">Due: {formatCurrency(getDueAmount(booking))}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${getStatusBadgeClass(booking.booking_status)}`}>
                                                    {booking.booking_status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`payment-badge ${getPaymentBadgeClass(booking.payment_status)}`}>
                                                    {booking.payment_status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button className="action-btn edit" onClick={() => { setSelectedBooking(booking); setShowModal(true); }} title="Edit Status">
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <Link to={`/booking/track/${booking.booking_id}`} className="action-btn view" target="_blank" title="View Details">
                                                        <i className="fas fa-eye"></i>
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="empty-state">
                                            <i className="fas fa-inbox"></i>
                                            <h3>No Bookings Found</h3>
                                            <p>Try adjusting your filters or clear them to see all bookings.</p>
                                            <button className="clear-filters-btn" onClick={clearFilters}>
                                                Clear All Filters
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Edit Status Modal */}
            {showModal && selectedBooking && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-container" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Update Booking Status</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="booking-summary">
                                <div className="summary-row">
                                    <span className="label">Booking ID</span>
                                    <span className="value booking-id">{selectedBooking.booking_id}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="label">Customer</span>
                                    <span className="value">{selectedBooking.customer_name}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="label">Package</span>
                                    <span className="value">{selectedBooking.package_title}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="label">Total Amount</span>
                                    <span className="value amount">{formatCurrency(selectedBooking.total_amount)}</span>
                                </div>
                            </div>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                handleStatusUpdate(
                                    selectedBooking.id,
                                    formData.get('booking_status'),
                                    formData.get('payment_status'),
                                    formData.get('cancellation_reason')
                                );
                            }}>
                                <div className="form-group">
                                    <label>Booking Status</label>
                                    <select name="booking_status" defaultValue={selectedBooking.booking_status}>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="pending">Pending</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Payment Status</label>
                                    <select name="payment_status" defaultValue={selectedBooking.payment_status}>
                                        <option value="completed">Completed</option>
                                        <option value="partial">Partial</option>
                                        <option value="pending">Pending</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Cancellation Reason</label>
                                    <textarea name="cancellation_reason" rows="3" defaultValue={selectedBooking.cancellation_reason} placeholder="Enter cancellation reason..."></textarea>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn-submit" disabled={updating}>
                                        {updating ? <><i className="fas fa-spinner fa-spin"></i> Updating...</> : 'Update Status'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBookings;
